import api from '@pray/shared/utils/api';
import type { PaginatedListResponse } from '@pray/shared/utils/api/types/response';

import type { ArtistContentResponse } from './types';

/**
 * Media type filter options for content queries
 * - 'audio': Audio content only
 * - 'video': Video content only
 * - 'all': Both audio and video content (sends 'audio,video' to backend)
 */
export type MediaTypeFilter = 'audio' | 'video' | 'all';

/* eslint-disable camelcase */
type GetArtistContentParams = {
  media_type?: string;
  is_added_to_series?: boolean;
  content_series_id?: string;
  include_content_from_rss?: boolean;
  search?: string;
  offset?: number;
  limit?: number;
  sort_column?: string;
  sort_direction?: string;
  tag_ids?: string;
  tag_match?: 'any' | 'all';
  include_tags?: boolean;
  date_from?: string;
  date_to?: string;
  /** Visibility status filter: comma-separated list (published, unpublished, scheduled) */
  statuses?: string;
  /** Series filter mode: with_series, without_series, or specific */
  series_mode?: 'with_series' | 'without_series' | 'specific';
  /** Series IDs to filter by (comma-separated, when series_mode is 'specific') */
  series_ids?: string;
  search_mode?: 'default' | 'hybrid';
};
/* eslint-enable camelcase */

type GetArtistContentResponse = PaginatedListResponse<ArtistContentResponse>;

export const getArtistContent = async ({
  artistId = null,
  mediaType = null,
  isAddedToSeries = null,
  contentSeriesId = null,
  includeContentFromRSS = false,
  search = '',
  offset = 0,
  limit = 0,
  sortColumn = '',
  sortDirection = 'asc',
  tagIds = null,
  tagMatch = 'any',
  includeTags = false,
  dateFrom = null,
  dateTo = null,
  statuses = null,
  seriesMode = null,
  searchMode = null,
  seriesIds = null,
  options = {},
}: {
  artistId?: string | null;
  /** Supports 'audio', 'video', 'all', or array like ['audio', 'video'] */
  mediaType?: MediaTypeFilter | string | string[] | null;
  isAddedToSeries?: boolean | null;
  contentSeriesId?: string | null;
  includeContentFromRSS?: boolean;
  search?: string;
  offset?: number;
  limit?: number;
  sortColumn?: string;
  sortDirection?: string;
  tagIds?: string[] | null;
  tagMatch?: 'any' | 'all';
  includeTags?: boolean;
  dateFrom?: string | null;
  dateTo?: string | null;
  /** Visibility status filter array: ['published', 'unpublished', 'scheduled'] */
  statuses?: string[] | null;
  /** Series filter mode: 'with_series', 'without_series', 'specific' */
  seriesMode?: 'with_series' | 'without_series' | 'specific' | null;
  /** Series IDs to filter by (when seriesMode is 'specific') */
  seriesIds?: string[] | null;
  searchMode?: 'default' | 'hybrid' | null;
  options?: Record<string, unknown>;
} = {}) => {
  if (!artistId) throw Error('ID is required');

  const params: GetArtistContentParams = {};

  // Handle media type - supports 'audio', 'video', 'all', or array
  if (mediaType !== null) {
    if (mediaType === 'all') {
      // 'all' means both audio and video (excludes images)
      params.media_type = 'audio,video';
    } else if (Array.isArray(mediaType)) {
      // Array of media types: ['audio', 'video'] -> 'audio,video'
      params.media_type = mediaType.join(',');
    } else {
      // Single value: 'audio' or 'video'
      params.media_type = mediaType;
    }
  }

  if (isAddedToSeries !== null) {
    params.is_added_to_series = isAddedToSeries;
  }

  if (includeContentFromRSS) {
    params.include_content_from_rss = includeContentFromRSS;
  }

  if (contentSeriesId) {
    params.content_series_id = contentSeriesId;
  }

  // NOTE: search and offset must be sent together. Dropping the offset while a
  // search term is set makes every "next page" request return the same first
  // page of results, which infinite scroll then appends as duplicate rows.
  if (search) {
    params.search = search;
  }

  if (offset) {
    params.offset = offset;
  }

  if (limit) params.limit = limit;

  if (sortColumn) {
    params.sort_column = sortColumn;
    params.sort_direction = sortDirection;
  }

  if (tagIds && tagIds.length > 0) {
    params.tag_ids = tagIds.join(',');
    params.tag_match = tagMatch;
  }

  if (includeTags) {
    params.include_tags = true;
  }

  if (dateFrom) {
    params.date_from = dateFrom;
  }

  if (dateTo) {
    params.date_to = dateTo;
  }

  if (statuses && statuses.length > 0) {
    params.statuses = statuses.join(',');
  }

  if (seriesMode) {
    params.series_mode = seriesMode;
    if (seriesMode === 'specific' && seriesIds && seriesIds.length > 0) {
      params.series_ids = seriesIds.join(',');
    }
  }

  if (searchMode) {
    params.search_mode = searchMode;
  }

  const url = `/web/studio/artists/${artistId}/content`;
  const response = await api.executeGetRequest<GetArtistContentResponse>(url, params, options);

  return response.data;
};
